import { NumberFormatter, NumberParser } from '@internationalized/number';
import { Formats } from '@smartsheet/core-views-api-models';
import { LanguageElements } from '../../language-elements/LanguageElements';

export interface CurrencyValue {
    currency: Formats.Currency;
    value: number; // to change the input value
    formattedValue: string; // to show in preview
    currencySymbol: string;
    isCurrencySymbolLeft: boolean;
}

/**
 * Regular expression to match currency symbols and numbers.
 * It captures the currency symbol, the numeric value, and any additional text.
 * from the core view repo:
 * https://git.lab.smartsheet.com/team-grid-views/core-views/-/blob/mainline/packages/utils/src/currency-utils/CurrencyUtil.ts?ref_type=heads
 */
const extraLeadingZeroRegex = /^0\d/;
const validCurrencySymbols = 'kr\\.|CN¥|¥|€|£|₪|₹|₩|₽|A\\$|AR\\$|AU\\$|CA\\$|CLP\\$|HK\\$|MX\\$|NZ\\$|R\\$|SG\\$|US\\$|\\$|\\R';
const numberGroup = `[^kr|CN¥|¥|€|£|₪|₹|₩|₽|A\\$|AR\\$|AU\\$|CA\\$|CLP\\$|HK\\$|MX\\$|NZ\\$|R\\$|SG\\$|US\\$|\\$|\\s]`;

/**
 * Parses a localized currency string and extracts its numeric value, symbol, and currency metadata.
 *
 * @param {string} value - The input currency string (e.g., "$1,234.56").
 * @param {string} locale - The locale used for formatting (e.g., "en-US").
 * @returns {CurrencyValue | undefined} Parsed currency information or undefined if input is invalid.
 */
export const parseCurrencyForTextNumber = (value: string, locale: string): CurrencyValue | undefined => {
    // Guard against false positives like "R-task" (example name of the task : "R-16"
    if (value.startsWith('R') && !/^R\s?\d/.test(value)) {
        return;
    }

    const currencyRegex = new RegExp(`^(${validCurrencySymbols})*\\s*(${numberGroup}+)\\s*(${validCurrencySymbols}$)*\\s*(.+)*`);
    const currencyValue = currencyRegex.exec(value);
    const shouldNotBeParsedAsCurrency = currencyValue?.[4] !== undefined;
    if (!currencyValue || shouldNotBeParsedAsCurrency) {
        return;
    }

    const currencySymbol = currencyValue[1] || currencyValue[3];
    const amount = currencyValue[2];
    if (extraLeadingZeroRegex.test(amount)) {
        return;
    }

    const currency = currencyCountryBySymbol.get(currencySymbol ?? '') ?? Formats.Currency.NONE;
    const currencyCode = currencyCodeByCountry.get(currency);
    if (!currencyCode) {
        return;
    }

    const formatter = new NumberFormatter(locale, { style: 'currency', currency: currencyCode });
    const parser = new NumberParser(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 18,
        minimumFractionDigits: 2,
    });

    const formatted = formatter.format(Number(amount));
    const numeric = parser.parse(formatted);
    if (isNaN(numeric)) {
        return;
    }

    const formattedValue = formatCurrency(numeric, formatter);
    const isCurrencySymbolLeft = getCurrencySymbolPosition(formatted, currencyCode);

    return { currency, value: numeric, formattedValue, currencySymbol: currencySymbol ?? '', isCurrencySymbolLeft };
};

/**
 * Formats a numeric value as a localized currency string using the given formatter.
 *
 * @param {number} value - The number to format.
 * @param {NumberFormatter} formatter - The formatter instance used for formatting.
 * @returns {string} Formatted currency string.
 */
const formatCurrency = (value: number, formatter: NumberFormatter): string => {
    return formatter.format(value);
};

/**
 * Determines whether the currency symbol is on the left side of the formatted currency string.
 *
 * @param {string} formatted - The formatted currency string (e.g., "$1,000.00").
 * @param {string} currency - The currency code (e.g., "USD").
 * @returns {boolean} True if the currency symbol is on the left, false otherwise.
 */
function getCurrencySymbolPosition(formatted: string, currency: string): boolean {
    const currencySymbol = formatted.replace(/\d|[\s.,]/g, '').trim();
    return formatted.startsWith(currencySymbol);
}

export const generateCurrencyNameMap = (languageElements: LanguageElements): Map<Formats.Currency, string> => {
    return new Map([
        [Formats.Currency.NONE, ''],
        [Formats.Currency.ARS, languageElements.CURRENCY_ARGENTINA_PESO],
        [Formats.Currency.AUD, languageElements.CURRENCY_AUSTRALIA_DOLLAR],
        [Formats.Currency.BRL, languageElements.CURRENCY_BRAZIL_REAL],
        [Formats.Currency.CAD, languageElements.CURRENCY_CANADA_DOLLAR],
        [Formats.Currency.CHF, languageElements.CURRENCY_SWITZERLAND_FRANC],
        [Formats.Currency.CLP, languageElements.CURRENCY_CHILE_PESO],
        [Formats.Currency.CNY, languageElements.CURRENCY_CHINA_YUAN],
        [Formats.Currency.DKK, languageElements.CURRENCY_DENMARK_KRONE],
        [Formats.Currency.EUR, languageElements.CURRENCY_EURO],
        [Formats.Currency.GBP, languageElements.CURRENCY_UNITED_KINGDOM_POUND],
        [Formats.Currency.HKD, languageElements.CURRENCY_HONG_KONG_DOLLAR],
        [Formats.Currency.ILS, languageElements.CURRENCY_ISRAEL_NEW_SHEKEL],
        [Formats.Currency.INR, languageElements.CURRENCY_INDIA_RUPEE],
        [Formats.Currency.JPY, languageElements.CURRENCY_JAPAN_YEN],
        [Formats.Currency.KRW, languageElements.CURRENCY_SOUTH_KOREA_WON],
        [Formats.Currency.MXN, languageElements.CURRENCY_MEXICO_PESO],
        [Formats.Currency.NOK, languageElements.CURRENCY_NORWAY_KRONE],
        [Formats.Currency.NZD, languageElements.CURRENCY_NEW_ZEALAND_DOLLAR],
        [Formats.Currency.RUB, languageElements.CURRENCY_RUSSIA_RUBLE],
        [Formats.Currency.SEK, languageElements.CURRENCY_SWEDEN_KRONA],
        [Formats.Currency.SGD, languageElements.CURRENCY_SINGAPORE_DOLLAR],
        [Formats.Currency.USD, languageElements.CURRENCY_UNITED_STATES_DOLLAR],
        [Formats.Currency.ZAR, languageElements.CURRENCY_SOUTH_AFRICA_RAND],
    ]);
};

export const currencyCountryBySymbol = new Map<string, Formats.Currency>([
    ['en-US$', Formats.Currency.USD],
    ['sk-SK$', Formats.Currency.USD],
    ['es-AR$', Formats.Currency.ARS],
    ['en-AU$', Formats.Currency.AUD],
    ['en-CA$', Formats.Currency.CAD],
    ['zh-HK$', Formats.Currency.HKD],
    ['es-MX$', Formats.Currency.MXN],
    ['en-NZ$', Formats.Currency.NZD],
    ['es-CL$', Formats.Currency.CLP],
    ['$', Formats.Currency.USD],
    ['US$', Formats.Currency.USD],
    ['AR$', Formats.Currency.ARS],
    ['A$', Formats.Currency.AUD],
    ['AU$', Formats.Currency.AUD],
    ['R$', Formats.Currency.BRL],
    ['CA$', Formats.Currency.CAD],
    ['CLP$', Formats.Currency.CLP],
    ['CN¥', Formats.Currency.CNY],
    ['kr.', Formats.Currency.DKK],
    ['€', Formats.Currency.EUR],
    ['HK$', Formats.Currency.HKD],
    ['₹', Formats.Currency.INR],
    ['₪', Formats.Currency.ILS],
    ['¥', Formats.Currency.JPY],
    ['MX$', Formats.Currency.MXN],
    ['NZ$', Formats.Currency.NZD],
    ['₽', Formats.Currency.RUB],
    ['SG$', Formats.Currency.SGD],
    ['₩', Formats.Currency.KRW],
    ['£', Formats.Currency.GBP],
    ['R', Formats.Currency.ZAR],
    ['SFr', Formats.Currency.CHF],
    ['CHF', Formats.Currency.CHF],
    ['FSK', Formats.Currency.CHF],
    ['Fr', Formats.Currency.CHF],
]);

/**
 * A map that takes Formats.Currency as key and returns the currency code value.
 */
export const currencyCodeByCountry = new Map<Formats.Currency, string>([
    [Formats.Currency.MXN, 'MXN'],
    [Formats.Currency.ARS, 'ARS'],
    [Formats.Currency.AUD, 'AUD'],
    [Formats.Currency.BRL, 'BRL'],
    [Formats.Currency.CAD, 'CAD'],
    [Formats.Currency.CHF, 'CHF'],
    [Formats.Currency.CLP, 'CLP'],
    [Formats.Currency.CNY, 'CNY'],
    [Formats.Currency.DKK, 'DKK'],
    [Formats.Currency.EUR, 'EUR'],
    [Formats.Currency.GBP, 'GBP'],
    [Formats.Currency.HKD, 'HKD'],
    [Formats.Currency.ILS, 'ILS'],
    [Formats.Currency.NZD, 'NZD'],
    [Formats.Currency.INR, 'INR'],
    [Formats.Currency.JPY, 'JPY'],
    [Formats.Currency.KRW, 'KRW'],
    [Formats.Currency.MXN, 'MXN'],
    [Formats.Currency.NOK, 'NOK'],
    [Formats.Currency.NZD, 'NZD'],
    [Formats.Currency.RUB, 'RUB'],
    [Formats.Currency.SEK, 'SEK'],
    [Formats.Currency.SGD, 'SGD'],
    [Formats.Currency.TWD, 'TWD'],
    [Formats.Currency.USD, 'USD'],
    [Formats.Currency.ZAR, 'ZAR'],
]);
